/* Feb-2026 · Free Tier Sunset Banner.
 *
 * Friendly heads-up for writers currently on the Free plan: the Free
 * tier stops being offered to *new* signups on `free_tier_sunset_date`
 * (existing Free accounts are never cut off — see subscriptions_service
 * `resolve_effective_tier`). This banner just nudges current Free
 * writers to consider upgrading before the cutoff, it never claims
 * their own access will end.
 *
 * Shown only when:
 *  - the user is on the Free plan (no subscription_tier at all — trial
 *    and paid users have one)
 *  - the admin-configured sunset date is set and still in the future
 *  - that date is within REMINDER_WINDOW_DAYS
 * Dismissal is snoozed for a week via localStorage so it's a nudge,
 * not a nag.
 */
import React, { useMemo, useState } from 'react';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth_context';

const REMINDER_WINDOW_DAYS = 60;
const SNOOZE_KEY = 'pw_free_sunset_snooze_until';
const SNOOZE_DAYS = 7;

function daysUntil(dateStr) {
  const target = new Date(dateStr);
  if (Number.isNaN(target.getTime())) return null;
  const diffMs = target.getTime() - Date.now();
  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
}

function readSnooze() {
  try {
    const raw = window.localStorage.getItem(SNOOZE_KEY);
    return raw ? Number(raw) : 0;
  } catch { return 0; }
}

// 2026-10 — Opens the shared PaywallDialog directly on its "Subscribe"
// tab (monthly/yearly toggle + yearly-savings badge included for
// free) instead of a bespoke dialog — same mechanism MyBillingPanel's
// "Choose a plan" button already uses.
function openSubscribeDialog() {
  window.dispatchEvent(new CustomEvent('paywall:open', {
    detail: { tab: 'subscribe', can_subscribe: true, action: 'upgrade your plan' },
  }));
}

/**
 * Preview mode (admin Pricing panel): pass `previewDaysLeft` to render
 * exactly what a Free writer would see for that many days-until-sunset,
 * bypassing the real user/snooze checks. `onSeePlans`/dismiss are inert.
 */
export default function FreeTierSunsetBanner({ previewDaysLeft, previewMode = false }) {
  const { user } = useAuth();
  const [snoozed, setSnoozed] = useState(() => readSnooze() > Date.now());

  const daysLeft = useMemo(() => {
    if (previewMode) return previewDaysLeft;
    if (!user?.free_tier_sunset_date) return null;
    return daysUntil(user.free_tier_sunset_date);
  }, [previewMode, previewDaysLeft, user?.free_tier_sunset_date]);

  const isFreeTier = previewMode || (!!user && !user.subscription_tier);
  const shouldShow = isFreeTier
    && daysLeft !== null
    && daysLeft > 0
    && daysLeft <= REMINDER_WINDOW_DAYS;

  if ((!previewMode && snoozed) || !shouldShow) return null;

  const dismiss = () => {
    if (previewMode) return;
    const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
    try { window.localStorage.setItem(SNOOZE_KEY, String(until)); } catch { /* ignore */ }
    setSnoozed(true);
  };

  return (
    <div
      className="my-3 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2.5 text-[12px] shadow-sm"
      role="status"
      data-testid="free-tier-sunset-banner"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold text-amber-500" data-testid="free-tier-sunset-banner-headline">
            You're on the Free plan — new signups move to Basic in {daysLeft} day{daysLeft === 1 ? '' : 's'}
          </div>
          <p className="mt-1 text-[11.5px] text-muted-foreground" data-testid="free-tier-sunset-banner-body">
            Your own Free access isn't going anywhere. But if you want more monthly AI headroom
            before the offer window closes, you can lock in an upgrade any time.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={previewMode ? undefined : openSubscribeDialog}
          disabled={previewMode}
          className="h-7 shrink-0 gap-1 px-2 text-[11px]"
          data-testid="free-tier-sunset-banner-see-plans"
        >
          See plans <ArrowRight className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={dismiss}
          disabled={previewMode}
          className="h-7 shrink-0 px-2 text-[10.5px]"
          data-testid="free-tier-sunset-banner-dismiss"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
